/**
 * SPP-655 / SPP-675-2 DWCM — mid-level transcript chunk store (Track B layer 2).
 * Module singleton so Player / MediaUtilities / playbackHighlight can stitch without React.
 *
 * Track B ownership (SPP-675-enhancement):
 * - Persistence (Redux/S3) stays canonical on the wire — not this module.
 * - This store owns windowing (`W`), readiness/seek/wait guards, and in-memory draft slices.
 * - Slate is the render surface only; flush/evict through this store — never a second persistence layer.
 */

import {
  ActiveWindow,
  CHUNK_MAX_BLOCKS,
  CHUNK_PLAYBACK_PREFETCH_LEAD_SEC,
  CHUNK_WINDOW_MAX,
  CHUNK_FIND_WINDOW_MAX,
  CHUNK_WINDOW_LOAD_MIN_INTERVAL_MS,
  dwcmInitialWindow,
  findChunkMetaForBlockStart,
  findChunkMetaForGlobalRow,
  findCaptionBlockForTime,
  findCaptionIndexForTime,
  findFirstCaptionStart,
  findChunkMetaForTime,
  findWordStartForTime,
  isBeforeFirstCaptionTime,
  mergeWindowNodes,
  cloneNodesForSlateEditor,
  mergedWindowHasDuplicateStarts,
  mergedWindowNeedsRepair,
  splitEditorNodesByChunkBoundaries,
  buildTranscriptChunkIndex,
  stitchChunks,
  recomputeGlobalStartIndices,
  computeTranscriptDocumentKey,
  resolveWindowForViewport,
  resolveWindowAroundOrdinal,
  clampContiguousWindow,
  TRANSCRIPT_DWCM_ENABLED,
  TranscriptBlock,
  TranscriptChunkMeta,
} from 'utils/transcriptChunk.util';
import {
  chunkDataError,
  chunkDataGroup,
  chunkDataLog,
  chunkDataWarn,
  chunkPerfLog,
  beginChunkSeekPerf,
  chunkSeekPerf,
  endChunkSeekPerf,
  cancelChunkSeekPerf,
  logTranscriptLinePlayDiagnosis,
} from './chunkDebugLog';
import {
  resolveDwcmTranscriptWaitLocked,
  resolvePendingViewportCatchUpOnEditorReady,
} from './useTranscriptChunkLoadingCue';

export {
  chunkPerfLog,
  beginChunkSeekPerf,
  chunkSeekPerf,
  endChunkSeekPerf,
  cancelChunkSeekPerf,
  logTranscriptLinePlayDiagnosis,
} from './chunkDebugLog';

type Listener = () => void;

export type TranscriptChunkStoreSnapshot = {
  index: TranscriptChunkMeta[];
  activeChunkId: string | null;
  activeWindow: ActiveWindow | null;
  editorWindow: ActiveWindow | null;
  warmChunkId: string | null;
  /** Chunk currently showing skeleton / not editor-ready */
  loadingChunkId: string | null;
  /** Active chunk Slate has finished mounting */
  editorReady: boolean;
  revision: number;
  /** Track B UX pipeline guards (shell syncs presentation load state here). */
  scrollLoading: boolean;
  stressScrollPending: boolean;
  scrollFrozen: boolean;
  transcriptWaitLocked: boolean;
  hasPendingWindowDirect: boolean;
  playbackSeekInProgress: boolean;
  /** Bumped when a progress-bar seek lands in the already-mounted window. */
  inWindowSeekGeneration: number;
};

/** Shell → store sync for scroll/stress pipeline + chrome inputs (Track B). */
export type TranscriptUxPipelineGuards = {
  scrollLoading?: boolean;
  stressScrollPending?: boolean;
  scrollFrozen?: boolean;
  /** Shell-only chrome inputs — store owns wait-lock derivation from these. */
  wheelBlockActive?: boolean;
  atBoundaryTrigger?: boolean;
  viewportNeedsCatchUp?: boolean;
  seekLoadingUi?: boolean;
  hasPlaybackSeekTarget?: boolean;
  allowEditorMount?: boolean;
  chunkLoading?: boolean;
  isEditMode?: boolean;
};

/** DevTools — why transcript line play/pause is blocked (`diagnoseTranscriptLinePlay()`). */
export type TranscriptLinePlayBlockDiagnosis = {
  playable: boolean;
  blockers: string[];
  enabled: boolean;
  editorReady: boolean;
  editorReadyFlag: boolean;
  loadingChunkId: string | null;
  uxPipelineBusy: boolean;
  uxPipelineBusyReasons: string[];
  transcriptWaitLocked: boolean;
  pendingWindowDirect: boolean;
  playbackSeekSliding: boolean;
  playbackSeekTarget: { chunkId: string; time: number } | null;
  seekTargetSatisfied: boolean | null;
  playbackSeekInProgress: boolean;
  scrollLoading: boolean;
  stressScrollPending: boolean;
  scrollFrozen: boolean;
  shellChunkLoading: boolean;
  shellSeekLoadingUi: boolean;
  shellHasPlaybackSeekTarget: boolean;
  postSeekCatchUpSuppressed: boolean;
};

/** Boundary gap/overlap under this many seconds is normal rounding, not data loss. */
const CHUNK_BOUNDARY_TOLERANCE_SEC = 1;

const formatMediaTime = (sec: number | null | undefined): string => {
  if (typeof sec !== 'number' || Number.isNaN(sec)) return '—';
  const total = Math.max(0, Math.floor(sec));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

export type ChunkDumpRow = {
  ordinal: number;
  chunkId: string;
  blocks: number;
  globalRows: string;
  firstStart: string;
  lastEnd: string;
  metaRange: string;
  gapToNext: string;
  issue: string;
};

class TranscriptChunkStoreImpl {
  private index: TranscriptChunkMeta[] = [];
  /**
   * Track B `baseDocument` — verified full-document snapshot used for rematerialize.
   * Field name kept as `sourceDocument` for callers; use `getBaseDocument()`.
   */
  private sourceDocument: TranscriptBlock[] | null = null;
  /**
   * Track B working slices — mutable per-chunk cache (`workingDocument` bands).
   * Dirty ids prefer cache over rematerialize-from-base.
   */
  private chunks: Record<string, TranscriptBlock[]> = {};
  private activeChunkId: string | null = null;
  private activeWindow: ActiveWindow | null = null;
  private editorWindow: ActiveWindow | null = null;
  private warmChunkId: string | null = null;
  private loadingChunkId: string | null = null;
  private editorReady = true;
  /** Shell scroll-driven window load cue (synced via setUxPipelineGuards). */
  private scrollLoading = false;
  /** Stress-scroll settle pending (synced via setUxPipelineGuards). */
  private stressScrollPending = false;
  /** Stress-scroll freeze active (synced via setUxPipelineGuards). */
  private scrollFrozen = false;
  /** Shell chrome inputs for store-owned wait-lock (Track B). */
  private shellWheelBlockActive = false;
  private shellAtBoundaryTrigger = false;
  private shellViewportNeedsCatchUp = false;
  private shellSeekLoadingUi = false;
  private shellHasPlaybackSeekTarget = false;
  private shellAllowEditorMount = true;
  private shellChunkLoading = false;
  private shellIsEditMode = false;
  /**
   * Chunk whose nodes the live Slate editor currently holds. `activeChunkId`
   * flips as soon as a load is requested, so any flush keyed on it would write
   * the outgoing chunk's nodes over the incoming chunk (lost transcript).
   */
  private editorChunkId: string | null = null;
  /** Latest requested chunk while a load is in flight (prevents infinite thrash). */
  private queuedChunkId: string | null = null;
  /** Playback paused because target chunk was not ready — resume when that chunk mounts. */
  private playbackPausedForLoad = false;
  /** Chunk id that should trigger auto-resume after successful editor mount. */
  private playbackResumeChunkId: string | null = null;
  /** True while user is actively scrolling — skip playback chunk work. */
  private scrolling = false;
  /** Shell: force wait — block caption clicks and line-play until load completes. */
  private transcriptWaitLocked = false;
  /** performance.now() until which scroll events are self-inflicted. */
  private programmaticScrollUntil = 0;
  /**
   * Chunk the user navigated to via scrollbar/settle. Playback must not yank
   * back to the playhead chunk until the playhead enters this chunk (or pin clears).
   */
  private scrollPinnedChunkId: string | null = null;
  /** After user scrollbar navigation — suppress playback follow scroll briefly. */
  private userScrollNavigationUntil = 0;
  /** Active find next/prev jump — shell must not snap scroll to chunk start. */
  private findJumpTarget: {
    chunkId: string;
    time: number;
    blockKey: string;
  } | null = null;
  /** Last find jump chunk ordinal — detect cross-chunk next/prev needing clean reload. */
  private findJumpLastOrdinal: number | null = null;
  /** Progress-bar / skip seek — shell snaps viewport and shows skeleton. */
  private playbackSeekTarget: { chunkId: string; time: number } | null = null;
  /** True while the progress bar is being dragged — defer window load until release. */
  private playbackSeekSliding = false;
  /** Progress-bar seek completed inside the mounted window (no remount). */
  private inWindowSeekGeneration = 0;
  /** Progress-bar seek landed — suppress spurious viewport catch-up briefly. */
  private playbackSeekSettledAt = 0;
  private dirtyIds = new Set<string>();
  /** True while manual/autosave is persisting — suppress window reload churn. */
  private saveInFlight = false;
  /** Media id (+ shape) the store was initialized from — prevents cross-video reuse. */
  private documentKey: string | null = null;
  private revision = 0;
  /** Bumped when DWCM window document is rebuilt (find jump / rematerialize). */
  private documentRevision = 0;
  /** Bumped when the active chunk Slate editor finishes mounting — finder decorate listens. */
  private editorReadyRevision = 0;
  /** One-shot: next chunk-ready highlight sync should run with afterSeek semantics. */
  private chunkReadyHighlightAfterSeek = false;
  private listeners = new Set<Listener>();
  private enabled = false;
  /** performance.now() when current load started (requestChunkLoad → loading). */
  private loadStartedAt: number | null = null;
  private loadChunkId: string | null = null;
  /** Coalesce rapid window remounts while editor is not ready. */
  private lastWindowDirectAt = 0;
  private pendingWindowDirect: {
    window: ActiveWindow;
    source: string;
  } | null = null;
  private windowDirectTimer: number | 0 = 0;
  /** Playback time to restore after caption ↔ paragraph mode switch. */
  private modeSwitchAnchorTime: number | null = null;

  /** ms since current chunk load started; null if none. */
  getLoadElapsedMs(): number | null {
    if (this.loadStartedAt == null) return null;
    return performance.now() - this.loadStartedAt;
  }

  getLoadTiming() {
    return {
      chunkId: this.loadChunkId,
      startedAt: this.loadStartedAt,
      elapsedMs: this.getLoadElapsedMs(),
    };
  }

  setModeSwitchAnchorTime(timeSec: number) {
    if (!Number.isFinite(timeSec) || timeSec < 0) return;
    this.modeSwitchAnchorTime = timeSec;
  }

  consumeModeSwitchAnchorTime(): number | null {
    const t = this.modeSwitchAnchorTime;
    this.modeSwitchAnchorTime = null;
    return t;
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private emit() {
    this.revision += 1;
    this.listeners.forEach((l) => l());
  }

  isEnabled() {
    return this.enabled && this.index.length > 0;
  }

  /** SPP-675-2 DWCM path when flag on and multi-chunk. */
  isDwcm() {
    return TRANSCRIPT_DWCM_ENABLED && this.enabled && this.index.length > 1;
  }

  getActiveWindow(): ActiveWindow | null {
    return this.activeWindow;
  }

  getEditorWindow(): ActiveWindow | null {
    return this.editorWindow;
  }

  /** Track B alias — verified snapshot (`sourceDocument`). */
  getBaseDocument(): TranscriptBlock[] | null {
    return this.sourceDocument;
  }

  /** True when shell scroll/stress pipeline reports an in-flight window load. */
  isUxPipelineBusy(): boolean {
    // Leftover scroll/stress chrome after the editor is ready must not keep play
    // locked — same policy as `scrollLoading` (SPP-675 progress-bar seek).
    if (this.isEditorReady()) return false;
    return (
      this.stressScrollPending ||
      this.scrollFrozen ||
      this.scrollLoading
    );
  }

  /**
   * Unified window-transition busy (store readiness + seek + deferred catch-up + UX pipeline).
   * Chrome (Step 4) should prefer this over inventing parallel shell-only truth.
   */
  isWindowTransitionBusy(): boolean {
    return (
      !this.isEditorReady() ||
      this.isUxPipelineBusy() ||
      this.isPlaybackSeekInProgress() ||
      !!this.pendingWindowDirect
    );
  }

  /**
   * Shell syncs presentation pipeline + chrome inputs; store owns wait-lock
   * derivation so Player / resume / caption gates share one truth (Track B).
   */
  setUxPipelineGuards(next: TranscriptUxPipelineGuards): void {
    let changed = false;
    if (
      next.scrollLoading !== undefined &&
      this.scrollLoading !== next.scrollLoading
    ) {
      this.scrollLoading = next.scrollLoading;
      changed = true;
    }
    if (
      next.stressScrollPending !== undefined &&
      this.stressScrollPending !== next.stressScrollPending
    ) {
      this.stressScrollPending = next.stressScrollPending;
      changed = true;
    }
    if (
      next.scrollFrozen !== undefined &&
      this.scrollFrozen !== next.scrollFrozen
    ) {
      this.scrollFrozen = next.scrollFrozen;
      changed = true;
    }
    if (
      next.wheelBlockActive !== undefined &&
      this.shellWheelBlockActive !== next.wheelBlockActive
    ) {
      this.shellWheelBlockActive = next.wheelBlockActive;
      changed = true;
    }
    if (
      next.atBoundaryTrigger !== undefined &&
      this.shellAtBoundaryTrigger !== next.atBoundaryTrigger
    ) {
      this.shellAtBoundaryTrigger = next.atBoundaryTrigger;
      changed = true;
    }
    if (
      next.viewportNeedsCatchUp !== undefined &&
      this.shellViewportNeedsCatchUp !== next.viewportNeedsCatchUp
    ) {
      this.shellViewportNeedsCatchUp = next.viewportNeedsCatchUp;
      changed = true;
    }
    if (
      next.seekLoadingUi !== undefined &&
      this.shellSeekLoadingUi !== next.seekLoadingUi
    ) {
      this.shellSeekLoadingUi = next.seekLoadingUi;
      changed = true;
    }
    if (
      next.hasPlaybackSeekTarget !== undefined &&
      this.shellHasPlaybackSeekTarget !== next.hasPlaybackSeekTarget
    ) {
      this.shellHasPlaybackSeekTarget = next.hasPlaybackSeekTarget;
      changed = true;
    }
    if (
      next.allowEditorMount !== undefined &&
      this.shellAllowEditorMount !== next.allowEditorMount
    ) {
      this.shellAllowEditorMount = next.allowEditorMount;
      changed = true;
    }
    if (
      next.chunkLoading !== undefined &&
      this.shellChunkLoading !== next.chunkLoading
    ) {
      this.shellChunkLoading = next.chunkLoading;
      changed = true;
    }
    if (
      next.isEditMode !== undefined &&
      this.shellIsEditMode !== next.isEditMode
    ) {
      this.shellIsEditMode = next.isEditMode;
      changed = true;
    }
    if (this.recomputeTranscriptWaitLocked()) {
      changed = true;
    }
    if (changed) this.emit();
  }

  /** Derive wait lock from store readiness + shell chrome inputs. */
  private recomputeTranscriptWaitLocked(): boolean {
    const locked = resolveDwcmTranscriptWaitLocked({
      isEditMode: this.shellIsEditMode,
      allowEditorMount: this.shellAllowEditorMount,
      editorReady: this.editorReady && this.loadingChunkId == null,
      chunkLoading: this.shellChunkLoading || this.loadingChunkId != null,
      scrollFrozen: this.scrollFrozen,
      stressScrollPending: this.stressScrollPending,
      scrollLoading: this.scrollLoading,
      viewportNeedsCatchUp: this.shellViewportNeedsCatchUp,
      seekLoadingUi: this.shellSeekLoadingUi,
      hasPlaybackSeekTarget: this.shellHasPlaybackSeekTarget,
      wheelBlockActive: this.shellWheelBlockActive,
      atBoundaryTrigger: this.shellAtBoundaryTrigger,
    });
    if (this.transcriptWaitLocked === locked) return false;
    this.transcriptWaitLocked = locked;
    return true;
  }

  isScrollLoading() {
    return this.scrollLoading;
  }

  isStressScrollPending() {
    return this.stressScrollPending;
  }

  isScrollFrozen() {
    return this.scrollFrozen;
  }

  hasPendingWindowDirect() {
    return !!this.pendingWindowDirect;
  }

  private syncActiveChunkFromWindow() {
    if (!this.activeWindow) {
      this.activeChunkId = null;
      return;
    }
    const meta = this.index[this.activeWindow.startOrdinal];
    this.activeChunkId = meta?.chunkId ?? null;
    this.warmChunkId = this.neighborWarm(this.activeChunkId ?? '');
  }

  private clampWindow(window: ActiveWindow): ActiveWindow {
    let prefer: number | undefined;
    if (this.activeChunkId) {
      const ord = this.index.findIndex((m) => m.chunkId === this.activeChunkId);
      if (
        ord >= 0 &&
        ord >= window.startOrdinal &&
        ord <= window.endOrdinal
      ) {
        prefer = ord;
      }
    }
    // No in-window focus → clampContiguousWindow pivots to center.
    return clampContiguousWindow(
      window,
      this.index.length,
      CHUNK_WINDOW_MAX,
      prefer,
    );
  }

  initWindow(startOrdinal: number, endOrdinal: number) {
    this.activeWindow = this.clampWindow({ startOrdinal, endOrdinal });
    this.editorWindow = { ...this.activeWindow };
    this.syncActiveChunkFromWindow();
    this.editorReady = false;
    this.loadingChunkId = null;
    chunkPerfLog('window:init', { window: this.activeWindow });
  }

  getMergedWindowNodes<T extends TranscriptBlock = TranscriptBlock>(): T[] {
    if (!this.activeWindow) {
      return this.getActiveNodes<T>();
    }
    this.materializeWindow(this.activeWindow);
    return mergeWindowNodes(
      this.chunks as Record<string, T[]>,
      this.index,
      this.activeWindow,
    );
  }

  private materializeChunk(chunkId: string): TranscriptBlock[] {
    const meta = this.getMeta(chunkId);
    if (!meta) return [];

    if (
      this.dirtyIds.has(chunkId) &&
      Object.prototype.hasOwnProperty.call(this.chunks, chunkId)
    ) {
      return this.chunks[chunkId];
    }

    if (this.sourceDocument) {
      const slice = this.sourceDocument.slice(
        meta.globalStartIndex,
        meta.globalStartIndex + meta.blockCount,
      );
      const existing = this.chunks[chunkId];
      if (!existing?.length || existing.length !== slice.length) {
        this.chunks[chunkId] = slice;
        return slice;
      }
      return existing;
    }

    if (Object.prototype.hasOwnProperty.call(this.chunks, chunkId)) {
      return this.chunks[chunkId];
    }
    return [];
  }

  private materializeWindow(window: ActiveWindow) {
    for (let ord = window.startOrdinal; ord <= window.endOrdinal; ord++) {
      const id = this.index[ord]?.chunkId;
      if (id) this.materializeChunk(id);
    }
  }

  flushEditorWindowToStore(
    nodes: TranscriptBlock[] | null | undefined,
    source = 'flushEditorWindow',
  ): boolean {
    if (!nodes?.length || !this.editorWindow || !this.editorReady) return false;
    recomputeGlobalStartIndices(this.index, this.chunks, this.dirtyIds);
    const parts = splitEditorNodesByChunkBoundaries(
      nodes,
      this.index,
      this.editorWindow,
      this.chunks,
    );
    let ok = false;
    parts.forEach((slice, chunkId) => {
      if (this.updateChunk(chunkId, slice, source)) ok = true;
    });
    return ok;
  }

  appendToWindow(): boolean {
    if (!this.activeWindow || !this.isDwcm()) return false;
    const nextOrd = this.activeWindow.endOrdinal + 1;
    if (nextOrd >= this.index.length) return false;
    if (
      nextOrd - this.activeWindow.startOrdinal + 1 >
      CHUNK_WINDOW_MAX
    ) {
      return false;
    }
    if (window?.Editor?.children && this.editorWindow) {
      this.flushEditorWindowToStore(
        window.Editor.children as TranscriptBlock[],
        'window:append-flush',
      );
    }
    this.activeWindow = {
      startOrdinal: this.activeWindow.startOrdinal,
      endOrdinal: nextOrd,
    };
    this.syncActiveChunkFromWindow();
    this.editorReady = false;
    this.loadingChunkId = null;
    chunkPerfLog('window:append', { window: this.activeWindow });
    this.emit();
    return true;
  }

  prependToWindow(): boolean {
    if (!this.activeWindow || !this.isDwcm()) return false;
    const prevOrd = this.activeWindow.startOrdinal - 1;
    if (prevOrd < 0) return false;
    if (
      this.activeWindow.endOrdinal - prevOrd + 1 >
      CHUNK_WINDOW_MAX
    ) {
      return false;
    }
    if (window?.Editor?.children && this.editorWindow) {
      this.flushEditorWindowToStore(
        window.Editor.children as TranscriptBlock[],
        'window:prepend-flush',
      );
    }
    this.activeWindow = {
      startOrdinal: prevOrd,
      endOrdinal: this.activeWindow.endOrdinal,
    };
    this.syncActiveChunkFromWindow();
    this.editorReady = false;
    this.loadingChunkId = null;
    chunkPerfLog('window:prepend', { window: this.activeWindow });
    this.emit();
    return true;
  }

  evictFromWindow(direction: 'down' | 'up'): boolean {
    if (!this.activeWindow || !this.isDwcm()) return false;
    if (this.activeWindow.endOrdinal <= this.activeWindow.startOrdinal) {
      return false;
    }
    if (window?.Editor?.children && this.editorWindow) {
      this.flushEditorWindowToStore(
        window.Editor.children as TranscriptBlock[],
        'window:evict-flush',
      );
    }
    if (direction === 'down') {
      this.activeWindow = {
        startOrdinal: this.activeWindow.startOrdinal + 1,
        endOrdinal: this.activeWindow.endOrdinal,
      };
    } else {
      this.activeWindow = {
        startOrdinal: this.activeWindow.startOrdinal,
        endOrdinal: this.activeWindow.endOrdinal - 1,
      };
    }
    this.syncActiveChunkFromWindow();
    this.editorWindow = { ...this.activeWindow };
    this.editorReady = false;
    chunkPerfLog('window:evict', { direction, window: this.activeWindow });
    this.emit();
    return true;
  }

  ensureWindowContainsOrdinal(
    ordinal: number,
    source = 'ensure-ordinal',
  ): ActiveWindow | null {
    if (!this.isDwcm() || !this.index.length) return null;
    const ord = Math.max(0, Math.min(ordinal, this.index.length - 1));
    if (!this.activeWindow) {
      const boot = dwcmInitialWindow(this.index.length);
      this.initWindow(boot.startOrdinal, boot.endOrdinal);
    }
    const w = this.activeWindow!;
    if (ord >= w.startOrdinal && ord <= w.endOrdinal) {
      return w;
    }

    const targetWindow = resolveWindowAroundOrdinal(ord, this.index.length);
    this.setActiveWindowDirect(targetWindow, source);
    return this.activeWindow;
  }

  /** True when a global caption row is inside the mounted DWCM window. */
  isGlobalRowInActiveWindow(globalRow: number): boolean {
    if (!this.isDwcm() || !this.activeWindow || !this.index.length) {
      return false;
    }
    const startMeta = this.index[this.activeWindow.startOrdinal];
    const endMeta = this.index[this.activeWindow.endOrdinal];
    if (!startMeta || !endMeta) return false;
    const start = startMeta.globalStartIndex;
    const end = endMeta.globalStartIndex + endMeta.blockCount;
    return globalRow >= start && globalRow < end;
  }

  /**
   * Find jump — load the single target chunk when the match is outside W.
   * In-window next/prev (same chunk or already mounted): scroll + highlight only.
   */
  ensureFindJumpForGlobalRow(globalRow: number): {
    meta: TranscriptChunkMeta;
    windowLoaded: boolean;
  } | null {
    recomputeGlobalStartIndices(this.index, this.chunks, this.dirtyIds);
    const meta = findChunkMetaForGlobalRow(this.index, globalRow);
    if (!meta || !this.isDwcm()) return null;

    this.scrollPinnedChunkId = null;
    this.cancelPlaybackSeek('find-jump');
    this.cancelPendingWindowDirect();

    const targetWindow = resolveWindowAroundOrdinal(
      meta.ordinal,
      this.index.length,
      CHUNK_FIND_WINDOW_MAX,
    );

    const chunkInActiveWindow =
      !!this.activeWindow &&
      meta.ordinal >= this.activeWindow.startOrdinal &&
      meta.ordinal <= this.activeWindow.endOrdinal;

    if (
      chunkInActiveWindow &&
      this.isGlobalRowInActiveWindow(globalRow) &&
      this.editorReady &&
      !this.loadingChunkId &&
      this.isWindowMergeHealthy(this.activeWindow!)
    ) {
      chunkPerfLog('find-jump:in-window', {
        globalRow,
        chunkId: meta.chunkId,
        window: this.activeWindow,
      });
      this.findJumpLastOrdinal = meta.ordinal;
      return { meta, windowLoaded: false };
    }

    this.markUserScrollNavigation();

    const sameWindow =
      !!this.activeWindow &&
      this.windowsEqual(this.activeWindow, targetWindow);

    if (!sameWindow) {
      this.setActiveWindowDirect(targetWindow, 'find-jump-window');
    } else if (!this.editorReady) {
      chunkPerfLog('find-jump:await-window-ready', {
        globalRow,
        window: this.activeWindow,
      });
      this.findJumpLastOrdinal = meta.ordinal;
      return { meta, windowLoaded: true };
    } else if (!this.isWindowMergeHealthy(targetWindow)) {
      chunkPerfLog('find-jump:repair-window', {
        globalRow,
        chunkId: meta.chunkId,
        window: targetWindow,
      });
      this.rematerializeWindowFromSource(targetWindow);
      this.documentRevision += 1;
      this.editorReady = false;
      this.emit();
    }

    chunkPerfLog('find-jump:load-window', {
      globalRow,
      chunkId: meta.chunkId,
      window: this.activeWindow,
      sameWindow,
    });
    this.findJumpLastOrdinal = meta.ordinal;
    return { meta, windowLoaded: true };
  }

  private isActiveWindowMergeHealthy(): boolean {
    return (
      !!this.activeWindow && this.isWindowMergeHealthy(this.activeWindow)
    );
  }

  /** True when every chunk in W is fully materialized and merge length matches index. */
  isWindowMergeHealthy(window: ActiveWindow): boolean {
    if (!window) return false;
    this.materializeWindow(window);
    const merged = mergeWindowNodes(
      this.chunks,
      this.index,
      window,
    );
    return !mergedWindowNeedsRepair(
      this.index,
      this.chunks,
      window,
      merged,
    );
  }

  private windowsEqual(a: ActiveWindow, b: ActiveWindow): boolean {
    return (
      a.startOrdinal === b.startOrdinal && a.endOrdinal === b.endOrdinal
    );
  }

  /**
   * Jump the active window in one step (stress-scroll settle recovery).
   * Progress-bar click/release (`playback-seek-window`) always applies immediately
   * so a new seek cancels the in-flight load. Drag preview may coalesce briefly.
   */
  setActiveWindowDirect(
    nextWindow: ActiveWindow,
    source = 'window:set-direct',
  ): boolean {
    if (!this.isDwcm()) return false;
    const w = this.clampWindow(nextWindow);
    if (this.activeWindow && this.windowsEqual(this.activeWindow, w)) {
      return false;
    }

    if (source === 'playback-seek-window' || source === 'find-jump-window') {
      this.cancelPendingWindowDirect();
      return this.applyActiveWindowDirect(w, source);
    }

    // Find jump owns W until the match chunk is ready — do not queue last-chunk catch-up.
    if (source === 'viewport-catch-up' && this.isFindJumpInProgress()) {
      chunkPerfLog('window:viewport-catch-up:ignore-during-find', {
        window: w,
        findTarget: this.findJumpTarget?.chunkId ?? null,
      });
      return false;
    }

    // Scroll catch-up while Slate is mounting — remember latest target only (S5 supersede).
    if (source === 'viewport-catch-up' && !this.editorReady) {
      if (this.activeWindow && this.windowsEqual(this.activeWindow, w)) {
        return false;
      }
      const unchanged =
        this.pendingWindowDirect?.source === 'viewport-catch-up' &&
        this.windowsEqual(this.pendingWindowDirect.window, w);
      if (!unchanged) {
        this.pendingWindowDirect = { window: w, source };
        chunkPerfLog('window:viewport-catch-up:defer', { window: w });
        // Notify shell so loading chrome stays up for the latest target.
        this.emit();
      }
      return !unchanged;
    }

    const shouldCoalesce =
      !this.editorReady &&
      source === 'playback-seek-preview' &&
      performance.now() - this.lastWindowDirectAt <
        CHUNK_WINDOW_LOAD_MIN_INTERVAL_MS;

    if (shouldCoalesce) {
      this.pendingWindowDirect = { window: w, source };
      this.schedulePendingWindowDirect();
      chunkPerfLog('window:set-direct:coalesce', { window: w, source });
      return true;
    }

    return this.applyActiveWindowDirect(w, source);
  }

  private schedulePendingWindowDirect() {
    if (this.windowDirectTimer) return;
    const delay = Math.max(
      0,
      CHUNK_WINDOW_LOAD_MIN_INTERVAL_MS -
        (performance.now() - this.lastWindowDirectAt),
    );
    this.windowDirectTimer = window.setTimeout(() => {
      this.windowDirectTimer = 0;
      const pending = this.pendingWindowDirect;
      this.pendingWindowDirect = null;
      if (!pending) return;
      this.applyActiveWindowDirect(pending.window, pending.source);
    }, delay);
  }

  private windowsOverlap(a: ActiveWindow, b: ActiveWindow): boolean {
    return a.startOrdinal <= b.endOrdinal && b.startOrdinal <= a.endOrdinal;
  }

  /** ±1 ordinal slide with shared chunks — keep Slate mounted when possible. */
  private isIncrementalWindowSlide(
    prev: ActiveWindow,
    next: ActiveWindow,
  ): boolean {
    return (
      this.windowsOverlap(prev, next) &&
      next.startOrdinal >= prev.startOrdinal - 1 &&
      next.startOrdinal <= prev.startOrdinal + 1 &&
      next.endOrdinal >= prev.endOrdinal - 1 &&
      next.endOrdinal <= prev.endOrdinal + 1
    );
  }

  private applyActiveWindowDirect(w: ActiveWindow, source: string): boolean {
    if (this.activeWindow && this.windowsEqual(this.activeWindow, w)) {
      return false;
    }
    const prevWindow = this.activeWindow;
    const editorChildren = window?.Editor?.children as
      | TranscriptBlock[]
      | undefined;
    const editorWindow = this.editorWindow;
    const canFlush =
      this.editorReady &&
      !!editorChildren?.length &&
      !!editorWindow &&
      !!this.activeWindow &&
      editorWindow.startOrdinal === this.activeWindow.startOrdinal &&
      editorWindow.endOrdinal === this.activeWindow.endOrdinal;
    let expectedBlocks = 0;
    if (canFlush && editorWindow) {
      for (
        let ord = editorWindow.startOrdinal;
        ord <= editorWindow.endOrdinal;
        ord++
      ) {
        expectedBlocks += this.index[ord]?.blockCount ?? 0;
      }
    }
    if (
      canFlush &&
      expectedBlocks > 0 &&
      editorChildren!.length === expectedBlocks
    ) {
      this.flushEditorWindowToStore(editorChildren!, `${source}:flush`);
    } else if (editorChildren?.length) {
      chunkPerfLog('window:skip-flush-on-direct', {
        source,
        expectedBlocks,
        actual: editorChildren.length,
        editorWindow,
        activeWindow: this.activeWindow,
      });
    }
    this.activeWindow = w;
    this.editorWindow = { ...w };
    this.syncActiveChunkFromWindow();
    const incrementalSlide =
      this.isDwcm() &&
      source === 'viewport-catch-up' &&
      !!prevWindow &&
      this.isIncrementalWindowSlide(prevWindow, w);
    const upwardPrepend =
      source === 'viewport-catch-up' &&
      !!prevWindow &&
      w.startOrdinal < prevWindow.startOrdinal;
    const linePlayExpand =
      this.isDwcm() &&
      source === 'line-play-window' &&
      !!prevWindow &&
      this.windowsOverlap(prevWindow, w);
    if (!((incrementalSlide || linePlayExpand) && this.editorReady && !upwardPrepend)) {
      this.editorReady = false;
    } else {
      chunkPerfLog('window:incremental-slide-keep-ready', {
        prev: prevWindow,
        next: w,
        source,
      });
    }
    this.loadingChunkId = null;
    this.lastWindowDirectAt = performance.now();
    if (
      source.startsWith('playback-seek') ||
      source === 'find-jump-window' ||
      source === 'ensure-ordinal'
    ) {
      const matT0 = performance.now();
      this.rematerializeWindowFromSource(w);
      if (source.startsWith('playback-seek')) {
        chunkSeekPerf('materialize-window', {
          source,
          window: w,
          blockCount: this.expectedWindowBlockCount(w),
          materializeMs: Number((performance.now() - matT0).toFixed(1)),
        });
      }
      this.documentRevision += 1;
    } else if (
      source === 'viewport-catch-up' &&
      prevWindow &&
      w.startOrdinal < prevWindow.startOrdinal
    ) {
      this.rematerializeWindowFromSource(w);
      this.documentRevision += 1;
    } else if (
      source === 'line-play-window' &&
      prevWindow &&
      !this.windowsOverlap(prevWindow, w)
    ) {
      this.rematerializeWindowFromSource(w);
      this.documentRevision += 1;
    }
    chunkPerfLog('window:set-direct', { window: w, source });
    this.emit();
    return true;
  }

  /**
   * After scroll stops, align window with viewport so user is not stuck in blank spacers.
   */
  ensureWindowCoversViewport(
    scrollTop: number,
    viewportHeight: number,
    heightFor: (meta: TranscriptChunkMeta) => number,
  ): boolean {
    if (!this.isDwcm() || !this.index.length) return false;
    if (!this.activeWindow) {
      const boot = dwcmInitialWindow(this.index.length);
      this.initWindow(boot.startOrdinal, boot.endOrdinal);
    }
    const current = this.activeWindow!;
    const target = resolveWindowForViewport(
      this.index,
      scrollTop,
      viewportHeight,
      heightFor,
    );
    if (
      target.startOrdinal === current.startOrdinal &&
      target.endOrdinal === current.endOrdinal
    ) {
      return false;
    }
    return this.setActiveWindowDirect(target, 'viewport-catch-up');
  }

  /**
   * Signal Slate finished mounting `window`.
   * @returns false when a newer viewport-catch-up target supersedes this mount (S5),
   * except during find jump — stale catch-up must not steal the match chunk.
   * Callers must not treat a rejected window as interactive/ready or run seek-finalize.
   */
  markEditorWindowReady(window: ActiveWindow): boolean {
    const pendingViewport = this.pendingWindowDirect;
    if (pendingViewport?.source === 'viewport-catch-up') {
      const findTargetOrdinal = this.findJumpTargetOrdinal();
      const decision = resolvePendingViewportCatchUpOnEditorReady({
        pendingCatchUpWindow: pendingViewport.window,
        mountedWindow: window,
        seekInProgress: this.isPlaybackSeekInProgress(),
        findJumpInProgress: this.isFindJumpInProgress(),
        findTargetOrdinal,
      });
      if (decision === 'defer-seek') {
        // Keep catch-up target for after seek; do not mark this mount ready.
        chunkPerfLog('window:viewport-catch-up:defer-during-seek', {
          stale: window,
          next: pendingViewport.window,
        });
        return false;
      }
      if (decision === 'drop-pending-accept') {
        this.pendingWindowDirect = null;
        chunkPerfLog('window:viewport-catch-up:drop-during-find', {
          stalePending: pendingViewport.window,
          accepted: window,
          findTargetOrdinal,
        });
      } else if (decision === 'drop-pending-reject') {
        this.pendingWindowDirect = null;
        chunkPerfLog('window:viewport-catch-up:reject-during-find', {
          stale: window,
          droppedPending: pendingViewport.window,
          findTargetOrdinal,
        });
        return false;
      } else if (decision === 'supersede') {
        // S5: never mark the stale window ready — jump to the latest settled target.
        this.pendingWindowDirect = null;
        chunkPerfLog('window:viewport-catch-up:supersede', {
          stale: window,
          next: pendingViewport.window,
        });
        this.applyActiveWindowDirect(
          pendingViewport.window,
          pendingViewport.source,
        );
        return false;
      } else {
        this.pendingWindowDirect = null;
      }
    }

    if (
      this.editorReady &&
      this.editorWindow &&
      this.windowsEqual(this.editorWindow, window)
    ) {
      if (!this.isPlaybackSeekInProgress()) {
        this.clearPlaybackSeekTarget();
      }
      this.playbackSeekSliding = false;
      return true;
    }
    this.editorWindow = { ...window };
    this.editorReady = true;
    this.loadingChunkId = null;
    this.editorReadyRevision += 1;
    if (!this.isPlaybackSeekInProgress()) {
      this.clearPlaybackSeekTarget();
    }
    this.playbackSeekSliding = false;
    this.recomputeTranscriptWaitLocked();
    this.emit();
    chunkPerfLog('window:editor-ready', { window: this.editorWindow });
    return true;
  }

  /** Stress-scroll unmount — editor DOM is gone; wait for remount before ready. */
  invalidateEditorWindowReady() {
    if (!this.isDwcm() || !this.editorReady) return;
    this.editorReady = false;
    this.emit();
    chunkPerfLog('window:editor-invalidated', { window: this.editorWindow });
  }

  setEditorBindingForWindow(window: ActiveWindow | null) {
    if (!window) {
      this.setEditorBinding(null);
      return;
    }
    const meta = this.index[window.startOrdinal];
    this.setEditorBinding(meta?.chunkId ?? null);
  }

  isLoading() {
    return this.loadingChunkId != null || !this.editorReady;
  }

  getLoadingChunkId() {
    return this.loadingChunkId;
  }

  isEditorReady() {
    return this.editorReady && this.loadingChunkId == null;
  }

  /**
   * Ready for playback / caption interaction after next-segment load (not merely painted).
   * Seek must be finished; UX pipeline and wait lock must be clear.
   *
   * When the editor is already ready, leftover progress-bar seek chrome (target
   * already in the mounted window, or stale sliding) must not keep play/seek
   * locked — same policy as `resolveTranscriptInteractionLocked`.
   */
  isTranscriptPlayable(): boolean {
    if (!this.isEnabled()) return true;
    if (!this.isEditorReady()) return false;
    if (this.isUxPipelineBusy()) return false;
    if (this.transcriptWaitLocked) return false;
    if (this.pendingWindowDirect) return false;
    if (this.playbackSeekSliding) {
      // In-window progress-bar scrub sets sliding without a cross-chunk target.
      if (this.playbackSeekTarget) return false;
      if (!this.isEditorReady()) return false;
    }
    const target = this.playbackSeekTarget;
    if (target && !this.isSeekSatisfiedAtTime(target.time)) {
      return false;
    }
    return true;
  }

  /** Drop stale progress-bar seek chrome (orphan sliding, satisfied target). */
  healStalePlaybackSeekChrome(): boolean {
    let changed = false;
    if (
      this.playbackSeekSliding &&
      !this.playbackSeekTarget &&
      this.isEditorReady()
    ) {
      this.playbackSeekSliding = false;
      changed = true;
      chunkPerfLog('playback-seek:heal-orphan-sliding');
    }
    if (!this.playbackSeekSliding) {
      const target = this.playbackSeekTarget;
      if (
        target &&
        this.isEditorReady() &&
        this.isSeekSatisfiedAtTime(target.time)
      ) {
        this.playbackSeekTarget = null;
        changed = true;
        chunkPerfLog('playback-seek:heal-stale-target', {
          was: target.chunkId,
        });
      }
    }
    if (changed) this.emit();
    return changed;
  }

  /** @deprecated Use healStalePlaybackSeekChrome */
  healStalePlaybackSeekTarget(): boolean {
    return this.healStalePlaybackSeekChrome();
  }

  /** @deprecated Wait lock is store-owned via setUxPipelineGuards; kept as no-op for callers. */
  setTranscriptWaitLocked(_locked: boolean) {
    // Track B: shell must not invent parallel wait-lock truth.
  }

  isTranscriptWaitLocked() {
    return this.transcriptWaitLocked;
  }

  /**
   * Caption-row play — same gate as Player play/seek (`isTranscriptPlayable`).
   * Do not AND `scrolling`: that flag does not emit on set, so line-play stayed
   * disabled after seek-bar seeks while the video was already playable.
   */
  isTranscriptInteractionAllowed(): boolean {
    this.healStalePlaybackSeekChrome();
    return this.isTranscriptPlayable();
  }

  /**
   * Snapshot every gate that blocks transcript line play/pause.
   * Call from DevTools: `diagnoseTranscriptLinePlay()` or enable chunk debug.
   */
  diagnoseTranscriptLinePlayBlock(): TranscriptLinePlayBlockDiagnosis {
    const blockers: string[] = [];
    const enabled = this.isEnabled();
    const editorReadyFlag = this.editorReady;
    const loadingChunkId = this.loadingChunkId;
    const editorReady = this.isEditorReady();
    const uxPipelineBusyReasons = editorReady
      ? []
      : [
          ...(this.stressScrollPending ? ['stressScrollPending'] : []),
          ...(this.scrollFrozen ? ['scrollFrozen'] : []),
          ...(this.scrollLoading ? ['scrollLoading'] : []),
        ];
    const uxPipelineBusy = this.isUxPipelineBusy();
    const target = this.playbackSeekTarget;
    const seekTargetSatisfied =
      target != null ? this.isSeekSatisfiedAtTime(target.time) : null;
    const postSeekCatchUpSuppressed =
      this.shouldSuppressPostSeekViewportCatchUp();

    if (!enabled) {
      return {
        playable: true,
        blockers: [],
        enabled: false,
        editorReady,
        editorReadyFlag,
        loadingChunkId,
        uxPipelineBusy,
        uxPipelineBusyReasons,
        transcriptWaitLocked: this.transcriptWaitLocked,
        pendingWindowDirect: !!this.pendingWindowDirect,
        playbackSeekSliding: this.playbackSeekSliding,
        playbackSeekTarget: target,
        seekTargetSatisfied,
        playbackSeekInProgress: this.isPlaybackSeekInProgress(),
        scrollLoading: this.scrollLoading,
        stressScrollPending: this.stressScrollPending,
        scrollFrozen: this.scrollFrozen,
        shellChunkLoading: this.shellChunkLoading,
        shellSeekLoadingUi: this.shellSeekLoadingUi,
        shellHasPlaybackSeekTarget: this.shellHasPlaybackSeekTarget,
        postSeekCatchUpSuppressed,
      };
    }

    if (!editorReady) {
      blockers.push(
        !editorReadyFlag
          ? 'editorReady=false'
          : `loadingChunkId=${loadingChunkId ?? '?'}`,
      );
    }
    if (uxPipelineBusy) {
      blockers.push(
        uxPipelineBusyReasons.length
          ? `uxPipelineBusy:${uxPipelineBusyReasons.join('+')}`
          : 'uxPipelineBusy',
      );
    }
    if (this.transcriptWaitLocked) blockers.push('transcriptWaitLocked');
    if (this.pendingWindowDirect) blockers.push('pendingWindowDirect');
    if (this.playbackSeekSliding) {
      if (this.playbackSeekTarget) {
        blockers.push('playbackSeekSliding');
      } else if (!this.isEditorReady()) {
        blockers.push('playbackSeekSliding:in-window');
      } else {
        blockers.push('playbackSeekSliding:orphan-stale');
      }
    }
    if (target && seekTargetSatisfied === false) {
      blockers.push(
        `playbackSeekTarget:${target.chunkId}@t${target.time.toFixed(2)}`,
      );
    }

    return {
      playable: blockers.length === 0,
      blockers,
      enabled,
      editorReady,
      editorReadyFlag,
      loadingChunkId,
      uxPipelineBusy,
      uxPipelineBusyReasons,
      transcriptWaitLocked: this.transcriptWaitLocked,
      pendingWindowDirect: !!this.pendingWindowDirect,
      playbackSeekSliding: this.playbackSeekSliding,
      playbackSeekTarget: target,
      seekTargetSatisfied,
      playbackSeekInProgress: this.isPlaybackSeekInProgress(),
      scrollLoading: this.scrollLoading,
      stressScrollPending: this.stressScrollPending,
      scrollFrozen: this.scrollFrozen,
      shellChunkLoading: this.shellChunkLoading,
      shellSeekLoadingUi: this.shellSeekLoadingUi,
      shellHasPlaybackSeekTarget: this.shellHasPlaybackSeekTarget,
      postSeekCatchUpSuppressed,
    };
  }

  isScrolling() {
    return this.scrolling;
  }

  /**
   * Playback follow and chunk snapping scroll the root themselves. Those
   * events must not be read as user scrolling, otherwise the shell freezes,
   * pins a chunk and pauses playback at every seam.
   */
  markProgrammaticScroll(durationMs = 250) {
    this.programmaticScrollUntil = performance.now() + durationMs;
  }

  isProgrammaticScroll() {
    return performance.now() < this.programmaticScrollUntil;
  }

  getEditorChunkId() {
    return this.editorChunkId;
  }

  /** Shell: the mounted Slate editor now holds this chunk's nodes. */
  setEditorBinding(chunkId: string | null) {
    if (this.editorChunkId === chunkId) return;
    chunkPerfLog('editor-binding', {
      from: this.editorChunkId,
      to: chunkId,
      activeChunkId: this.activeChunkId,
    });
    this.editorChunkId = chunkId;
  }

  clearEditorBinding(chunkId: string) {
    if (this.editorChunkId !== chunkId) return;
    this.setEditorBinding(null);
  }

  /** Chunk that owns whatever is currently in the editor. */
  private flushTargetChunkId(): string | null {
    return this.editorChunkId ?? this.activeChunkId;
  }

  /** No emit on scroll-start — every caption row subscribes. Unlock does emit. */
  setScrolling(next: boolean) {
    if (this.scrolling === next) return;
    this.scrolling = next;
    if (!next) this.emit();
  }

  getScrollPinnedChunkId() {
    return this.scrollPinnedChunkId;
  }

  /** Pin active chunk after user scrollbar/settle navigation. */
  setScrollPinnedChunk(chunkId: string | null) {
    this.scrollPinnedChunkId = chunkId;
    chunkPerfLog('scroll-pin', { chunkId });
  }

  clearScrollPinnedChunk() {
    if (this.scrollPinnedChunkId == null) return;
    chunkPerfLog('scroll-pin:clear', { was: this.scrollPinnedChunkId });
    this.scrollPinnedChunkId = null;
  }

  /** Suppress playback follow smooth-scroll after user stress-scroll / settle. */
  markUserScrollNavigation(cooldownMs = 3000) {
    this.userScrollNavigationUntil = performance.now() + cooldownMs;
  }

  clearUserScrollNavigation() {
    this.userScrollNavigationUntil = 0;
  }

  isUserScrollNavigationActive() {
    return performance.now() < this.userScrollNavigationUntil;
  }

  /** Find bar: target row while a cross-chunk jump loads (blocks chunk-start snap). */
  setFindJumpTarget(
    target: { chunkId: string; time: number; blockKey: string } | null,
  ) {
    this.findJumpTarget = target;
    if (target) {
      chunkPerfLog('find-jump:set', target);
    }
  }

  getFindJumpTarget() {
    return this.findJumpTarget;
  }

  private findJumpTargetOrdinal(): number | null {
    const chunkId = this.findJumpTarget?.chunkId;
    if (!chunkId) return null;
    const ord = this.index.find((m) => m.chunkId === chunkId)?.ordinal;
    return typeof ord === 'number' ? ord : null;
  }

  clearFindJumpTarget() {
    if (!this.findJumpTarget) return;
    chunkPerfLog('find-jump:clear', { was: this.findJumpTarget.chunkId });
    this.findJumpTarget = null;
  }

  /** New search query — drop cross-chunk find navigation state. */
  resetFindJumpSession() {
    this.findJumpLastOrdinal = null;
  }

  isFindJumpForChunk(chunkId: string) {
    return !!chunkId && this.findJumpTarget?.chunkId === chunkId;
  }

  isFindJumpInProgress() {
    return !!this.findJumpTarget;
  }

  /** Resolve chunk meta for a global row without loading a window. */
  getMetaForGlobalRow(globalRow: number): TranscriptChunkMeta | null {
    recomputeGlobalStartIndices(this.index, this.chunks, this.dirtyIds);
    return findChunkMetaForGlobalRow(this.index, globalRow, this.chunks);
  }

  /** Progress bar / skip — snap transcript to target chunk with skeleton. */
  setPlaybackSeekTarget(
    target: { chunkId: string; time: number } | null,
  ) {
    this.playbackSeekTarget = target;
    if (target) {
      chunkPerfLog('playback-seek:set', target);
      this.emit();
    }
  }

  getPlaybackSeekTarget() {
    return this.playbackSeekTarget;
  }

  clearPlaybackSeekTarget() {
    if (!this.playbackSeekTarget) return;
    chunkPerfLog('playback-seek:clear', {
      was: this.playbackSeekTarget.chunkId,
    });
    this.playbackSeekTarget = null;
  }

  /** Shell finished seek UI — clear target + sliding and notify subscribers. */
  releasePlaybackSeek(): void {
    const hadSeek = this.isPlaybackSeekInProgress();
    if (!hadSeek) return;
    chunkPerfLog('playback-seek:release', {
      was: this.playbackSeekTarget?.chunkId,
      window: this.activeWindow,
      editorReady: this.editorReady,
    });
    this.playbackSeekTarget = null;
    this.playbackSeekSliding = false;
    this.markPlaybackSeekSettled();
    this.emit();
  }

  /**
   * Progress-bar seek landed in the mounted window — no Slate remount.
   * Always emit so the shell drops seek skeleton / scroll locks.
   */
  completeInWindowPlaybackSeek(): void {
    this.cancelPendingWindowDirect();
    this.playbackSeekSliding = false;
    this.playbackSeekTarget = null;
    this.inWindowSeekGeneration += 1;
    this.markPlaybackSeekSettled();
    chunkPerfLog('playback-seek:in-window-complete', {
      window: this.activeWindow,
      editorReady: this.editorReady,
      generation: this.inWindowSeekGeneration,
    });
    this.emit();
  }

  getInWindowSeekGeneration() {
    return this.inWindowSeekGeneration;
  }

  /** User scroll or explicit cancel — stop progress-bar seek without remount thrash. */
  cancelPlaybackSeek(reason = 'cancel'): boolean {
    const hadSeek = this.isPlaybackSeekInProgress();
    this.cancelPendingWindowDirect();
    if (!hadSeek) return false;
    chunkPerfLog('playback-seek:cancel', { reason });
    cancelChunkSeekPerf();
    this.playbackSeekTarget = null;
    this.playbackSeekSliding = false;
    this.emit();
    return true;
  }

  cancelPendingWindowDirect() {
    if (this.windowDirectTimer) {
      window.clearTimeout(this.windowDirectTimer);
      this.windowDirectTimer = 0;
    }
    this.pendingWindowDirect = null;
  }

  /** Apply a coalesced window jump immediately (e.g. progress-bar release). */
  flushPendingWindowDirect(): boolean {
    if (!this.pendingWindowDirect) return false;
    if (this.windowDirectTimer) {
      window.clearTimeout(this.windowDirectTimer);
      this.windowDirectTimer = 0;
    }
    const pending = this.pendingWindowDirect;
    this.pendingWindowDirect = null;
    return this.applyActiveWindowDirect(pending.window, pending.source);
  }

  setPlaybackSeekSliding(sliding: boolean) {
    if (this.playbackSeekSliding === sliding) return;
    this.playbackSeekSliding = sliding;
    if (!sliding) {
      this.flushPendingWindowDirect();
    }
    chunkPerfLog('playback-seek:sliding', { sliding });
    this.emit();
  }

  isPlaybackSeekSliding() {
    return this.playbackSeekSliding;
  }

  /** Record progress-bar seek completion — gates post-seek viewport catch-up. */
  markPlaybackSeekSettled() {
    this.playbackSeekSettledAt = performance.now();
  }

  /**
   * After a progress-bar click/release, ignore spacer/outside false-positives that
   * would remount the window and re-lock play/seek a few seconds later.
   */
  shouldSuppressPostSeekViewportCatchUp(time?: number): boolean {
    const POST_SEEK_CATCHUP_SUPPRESS_MS = 3500;
    if (
      performance.now() - this.playbackSeekSettledAt >
      POST_SEEK_CATCHUP_SUPPRESS_MS
    ) {
      return false;
    }
    const t =
      time ??
      this.playbackSeekTarget?.time ??
      (typeof (window as any).Video?.currentTime === 'number'
        ? (window as any).Video.currentTime
        : null);
    if (t == null || !Number.isFinite(t)) return true;
    return this.isSeekSatisfiedAtTime(t);
  }

  /** Progress bar drag or post-drag window load — suppress scroll catch-up loops. */
  isPlaybackSeekInProgress() {
    return !!this.playbackSeekTarget || this.playbackSeekSliding;
  }

  /** True when playhead is before the first timed caption in the source doc. */
  isBeforeFirstCaptionTime(time: number): boolean {
    if (this.sourceDocument?.length) {
      return isBeforeFirstCaptionTime(this.sourceDocument, time);
    }
    const live = (window as any)?.Editor?.children as
      | TranscriptBlock[]
      | undefined;
    if (live?.length) {
      return isBeforeFirstCaptionTime(live, time);
    }
    return time <= 0.05;
  }

  /** Start time of the first timed caption row in the source document. */
  getFirstCaptionStart(): number | null {
    if (this.sourceDocument?.length) {
      return findFirstCaptionStart(this.sourceDocument);
    }
    const live = (window as any)?.Editor?.children as
      | TranscriptBlock[]
      | undefined;
    return live?.length ? findFirstCaptionStart(live) : null;
  }

  /**
   * Caption row for media time against the full source document (not the
   * mounted Slate slice). Always resolves to one row when the doc is non-empty.
   */
  resolvePlaybackCaptionAtTime(time: number): {
    captionStart: number;
    block: TranscriptBlock;
    globalIndex: number;
    wordStart: number | null;
  } | null {
    if (time == null || Number.isNaN(time)) return null;

    const fromDoc = (doc: TranscriptBlock[]) => {
      if (!doc.length) return null;
      const globalIndex = findCaptionIndexForTime(doc, time);
      const block = doc[globalIndex];
      if (!block) return null;
      const captionStart = block.data?.start;
      if (typeof captionStart !== 'number') return null;
      const wordStart =
        time < captionStart
          ? findWordStartForTime(block, captionStart)
          : findWordStartForTime(block, time);
      return {
        captionStart,
        block,
        globalIndex,
        wordStart,
      };
    };

    if (this.sourceDocument?.length) {
      return fromDoc(this.sourceDocument);
    }

    const live = (window as any)?.Editor?.children as
      | TranscriptBlock[]
      | undefined;
    if (live?.length) {
      const block = findCaptionBlockForTime(live, time);
      if (!block) return null;
      const captionStart = block.data?.start;
      if (typeof captionStart !== 'number') return null;
      const globalIndex = live.indexOf(block);
      return {
        captionStart,
        block,
        globalIndex: globalIndex >= 0 ? globalIndex : 0,
        wordStart: findWordStartForTime(block, time),
      };
    }

    return null;
  }

  /** True when playhead time is already covered by the mounted, ready editor window. */
  isSeekSatisfiedAtTime(time: number): boolean {
    if (!this.isDwcm() || !this.activeWindow || !this.editorReady) {
      return false;
    }
    const resolved = this.resolveChunkForTime(time, { playing: false });
    if (!resolved) return false;
    const ord = resolved.meta.ordinal;
    return (
      ord >= this.activeWindow.startOrdinal &&
      ord <= this.activeWindow.endOrdinal
    );
  }

  getDocumentRevision() {
    return this.documentRevision;
  }

  /** Same-window find jump — rebuild chunk slices without changing W. */
  prepareDwcmFindJumpWindow(): boolean {
    if (!this.isDwcm() || !this.activeWindow) return false;
    this.rematerializeWindowFromSource(this.activeWindow);
    this.documentRevision += 1;
    this.emit();
    return true;
  }

  /** Re-slice window chunks from sourceDocument (fixes bad flush after teleport). */
  rematerializeWindowFromSource(window: ActiveWindow) {
    if (!this.sourceDocument?.length) return;
    const w = this.clampWindow(window);
    for (let ord = w.startOrdinal; ord <= w.endOrdinal; ord++) {
      const meta = this.index[ord];
      if (!meta) continue;
      if (this.dirtyIds.has(meta.chunkId)) continue;
      const slice = this.sourceDocument.slice(
        meta.globalStartIndex,
        meta.globalStartIndex + meta.blockCount,
      );
      this.chunks[meta.chunkId] = slice;
    }
    chunkPerfLog('window:rematerialize-from-source', { window: w });
  }

  expectedWindowBlockCount(window: ActiveWindow): number {
    let count = 0;
    for (let ord = window.startOrdinal; ord <= window.endOrdinal; ord++) {
      count += this.index[ord]?.blockCount ?? 0;
    }
    return count;
  }

  isPlaybackSeekForChunk(chunkId: string) {
    return !!chunkId && this.playbackSeekTarget?.chunkId === chunkId;
  }

  /** True when progress-bar seek lands outside the mounted chunk/window. */
  isCrossChunkPlaybackSeek(
    time: number,
    resolved?: {
      meta: TranscriptChunkMeta;
      needsLoad: boolean;
      ready: boolean;
    } | null,
  ): boolean {
    const hit =
      resolved ?? this.resolveChunkForTime(time, { playing: false });
    if (!hit) return false;
    if (this.isDwcm()) {
      if (!this.activeWindow) return true;
      const ord = hit.meta.ordinal;
      return (
        ord < this.activeWindow.startOrdinal ||
        ord > this.activeWindow.endOrdinal ||
        !this.editorReady
      );
    }
    return hit.needsLoad || hit.meta.chunkId !== this.activeChunkId;
  }

  /**
   * Arm skeleton + viewport snap immediately on progress-bar drag (before chunk load).
   */
  armPlaybackSeek(time: number): boolean {
    if (!this.enabled || !this.index.length) return false;
    const resolveT0 = performance.now();
    const resolved = this.resolveChunkForTime(time, { playing: false });
    if (!resolved || !this.isCrossChunkPlaybackSeek(time, resolved)) {
      return false;
    }
    chunkSeekPerf('resolve-chunk', {
      time,
      chunkId: resolved.meta.chunkId,
      ordinal: resolved.meta.ordinal,
      indexLength: this.index.length,
      resolveMs: Number((performance.now() - resolveT0).toFixed(1)),
      crossChunk: true,
      prevWindow: this.activeWindow,
    });
    this.scrollPinnedChunkId = null;
    this.clearUserScrollNavigation();
    const next = { chunkId: resolved.meta.chunkId, time };
    const prev = this.playbackSeekTarget;
    if (
      prev?.chunkId === next.chunkId &&
      Math.abs(prev.time - next.time) < 0.05
    ) {
      return true;
    }
    this.setPlaybackSeekTarget(next);
    return true;
  }

  wasPlaybackPausedForLoad() {
    return this.playbackPausedForLoad;
  }

  getPlaybackResumeChunkId() {
    return this.playbackResumeChunkId;
  }

  clearPlaybackPausedForLoad() {
    this.playbackPausedForLoad = false;
    this.playbackResumeChunkId = null;
  }

  /** Call when pausing media because a chunk load is required during playback. */
  markPlaybackPausedForLoad(chunkId?: string | null) {
    this.playbackPausedForLoad = true;
    this.playbackResumeChunkId =
      chunkId ?? this.loadingChunkId ?? this.activeChunkId;
    chunkPerfLog('playback:mark-resume', {
      chunkId: this.playbackResumeChunkId,
    });
  }

  /**
   * After the target chunk editor is ready: returns true once if we should
   * auto-resume playback (was playing before this load).
   * DWCM: resume when the marked chunk is inside the mounted window (not only
   * `activeChunkId` / exact id match), and only once the window is playable
   * enough (editor ready, seek not in flight). Wait-lock may still clear on
   * the next shell paint — callers can retry via `tryResumePlaybackAfterChunkReady`.
   */
  consumePlaybackResumeForChunk(chunkId: string): boolean {
    if (!this.playbackPausedForLoad) return false;
    if (!this.isEditorReady()) return false;
    if (this.isPlaybackSeekInProgress()) return false;

    const expected = this.playbackResumeChunkId;
    if (expected != null) {
      const expectedInWindow =
        this.isDwcm() &&
        !!this.activeWindow &&
        this.index.some(
          (m, ord) =>
            ord >= this.activeWindow!.startOrdinal &&
            ord <= this.activeWindow!.endOrdinal &&
            m.chunkId === expected,
        );
      // Resume target must still be mounted — never auto-play for an evicted chunk.
      if (this.isDwcm() && !expectedInWindow) {
        chunkPerfLog('playback:resume-skip-outside-window', {
          ready: chunkId,
          expected,
        });
        return false;
      }
      const matchesExact = expected === chunkId;
      const readyInWindow =
        this.isDwcm() &&
        !!this.activeWindow &&
        this.index.some(
          (m, ord) =>
            ord >= this.activeWindow!.startOrdinal &&
            ord <= this.activeWindow!.endOrdinal &&
            m.chunkId === chunkId,
        );
      if (!matchesExact && !(expectedInWindow && readyInWindow)) {
        chunkPerfLog('playback:resume-skip-wrong-chunk', {
          ready: chunkId,
          expected,
        });
        return false;
      }
    }
    this.playbackPausedForLoad = false;
    this.playbackResumeChunkId = null;
    return true;
  }

  getSnapshot(): TranscriptChunkStoreSnapshot {
    return {
      index: this.index,
      activeChunkId: this.activeChunkId,
      activeWindow: this.activeWindow,
      editorWindow: this.editorWindow,
      warmChunkId: this.warmChunkId,
      loadingChunkId: this.loadingChunkId,
      editorReady: this.editorReady,
      revision: this.revision,
      scrollLoading: this.scrollLoading,
      stressScrollPending: this.stressScrollPending,
      scrollFrozen: this.scrollFrozen,
      transcriptWaitLocked: this.transcriptWaitLocked,
      hasPendingWindowDirect: !!this.pendingWindowDirect,
      playbackSeekInProgress: this.isPlaybackSeekInProgress(),
      inWindowSeekGeneration: this.inWindowSeekGeneration,
    };
  }

  getEditorReadyRevision() {
    return this.editorReadyRevision;
  }

  /** Seek presentation finalized — defer highlight sync to the mount rAF (once). */
  markChunkReadyHighlightAfterSeek() {
    this.chunkReadyHighlightAfterSeek = true;
  }

  consumeChunkReadyHighlightAfterSeek(): boolean {
    const afterSeek = this.chunkReadyHighlightAfterSeek;
    this.chunkReadyHighlightAfterSeek = false;
    return afterSeek;
  }

  /** Finder: force RichTextEditor to remap search highlights (in-window next/prev). */
  bumpFinderHighlightRevision() {
    this.editorReadyRevision += 1;
    chunkPerfLog('find-jump:highlight-bump', {
      revision: this.editorReadyRevision,
      window: this.activeWindow,
    });
    this.emit();
  }

  getIndex() {
    return this.index;
  }

  getActiveChunkId() {
    return this.activeChunkId;
  }

  getWarmChunkId() {
    return this.warmChunkId;
  }

  getChunkNodes<T extends TranscriptBlock = TranscriptBlock>(
    chunkId: string | null | undefined,
  ): T[] {
    if (!chunkId) return [];
    return this.materializeChunk(chunkId) as T[];
  }

  getActiveNodes<T extends TranscriptBlock = TranscriptBlock>(): T[] {
    return this.getChunkNodes<T>(this.activeChunkId);
  }

  getMeta(chunkId: string | null | undefined): TranscriptChunkMeta | null {
    if (!chunkId) return null;
    return this.index.find((m) => m.chunkId === chunkId) ?? null;
  }

  getActiveMeta(): TranscriptChunkMeta | null {
    return this.getMeta(this.activeChunkId);
  }

  getStitched<T extends TranscriptBlock = TranscriptBlock>(): T[] {
    if (!this.enabled || !this.index.length) return [];
    if (this.dirtyIds.size === 0 && this.sourceDocument) {
      return this.sourceDocument as T[];
    }
    if (this.sourceDocument && this.dirtyIds.size > 0) {
      return this.stitchFromSourceAndDirty<T>();
    }
    for (const meta of this.index) {
      this.materializeChunk(meta.chunkId);
    }
    return stitchChunks(this.index, this.chunks as Record<string, T[]>);
  }

  private stitchFromSourceAndDirty<T extends TranscriptBlock>(): T[] {
    const out: T[] = [];
    for (const meta of this.index) {
      if (this.dirtyIds.has(meta.chunkId) && this.chunks[meta.chunkId]) {
        out.push(...(this.chunks[meta.chunkId] as T[]));
      } else if (this.sourceDocument) {
        out.push(
          ...(this.sourceDocument.slice(
            meta.globalStartIndex,
            meta.globalStartIndex + meta.blockCount,
          ) as T[]),
        );
      } else {
        out.push(...((this.chunks[meta.chunkId] ?? []) as T[]));
      }
    }
    return out;
  }

  clear() {
    this.index = [];
    this.sourceDocument = null;
    this.chunks = {};
    this.activeChunkId = null;
    this.activeWindow = null;
    this.editorWindow = null;
    this.warmChunkId = null;
    this.loadingChunkId = null;
    this.queuedChunkId = null;
    this.editorReady = true;
    this.editorChunkId = null;
    this.playbackPausedForLoad = false;
    this.playbackResumeChunkId = null;
    this.scrolling = false;
    this.scrollLoading = false;
    this.stressScrollPending = false;
    this.scrollFrozen = false;
    this.transcriptWaitLocked = false;
    this.scrollPinnedChunkId = null;
    this.findJumpTarget = null;
    this.findJumpLastOrdinal = null;
    this.playbackSeekTarget = null;
    this.modeSwitchAnchorTime = null;
    this.dirtyIds.clear();
    this.enabled = false;
    this.documentKey = null;
    this.emit();
  }

  /** Block at a global row index, without stitching the whole document. */
  private blockAtGlobalRow(globalRow: number): TranscriptBlock | null {
    const meta = findChunkMetaForGlobalRow(this.index, globalRow);
    if (!meta) return null;
    const local = globalRow - meta.globalStartIndex;
    const materialized = this.chunks[meta.chunkId];
    if (materialized) {
      return materialized[local] ?? null;
    }
    if (this.sourceDocument) {
      return this.sourceDocument[globalRow] ?? null;
    }
    return null;
  }

  /**
   * Whether the store already holds this document. Saving re-emits an
   * equivalent transcript through Redux; re-splitting it would reset the
   * reader to the first chunk and blank the slot they were reading.
   */
  isSaveInFlight() {
    return this.saveInFlight;
  }

  setSaveInFlight(active: boolean) {
    this.saveInFlight = active;
  }

  /**
   * After a successful save: sync sourceDocument to the persisted body, clear
   * dirty flags, and keep the mounted window/editor — no re-init from Redux.
   */
  commitSavedDocument(nodes: TranscriptBlock[] | null | undefined): boolean {
    if (!this.enabled || !Array.isArray(nodes) || !nodes.length) return false;

    const prevWindow = this.activeWindow ? { ...this.activeWindow } : null;
    const anchorTime = this.readWindowAnchorTime();
    const total = this.index.reduce((sum, m) => sum + m.blockCount, 0);
    const structureChanged = total !== nodes.length;

    if (structureChanged) {
      const index = buildTranscriptChunkIndex(nodes, CHUNK_MAX_BLOCKS);
      this.index = index;
      this.sourceDocument = nodes as TranscriptBlock[];
      this.chunks = {};
      this.dirtyIds.clear();

      if (index.length) {
        const windowSpan = prevWindow
          ? prevWindow.endOrdinal - prevWindow.startOrdinal + 1
          : 1;
        const anchorMeta =
          (anchorTime != null
            ? findChunkMetaForTime(index, anchorTime)
            : null) ??
          index[
            Math.max(
              0,
              Math.min(prevWindow?.startOrdinal ?? 0, index.length - 1),
            )
          ];
        const target = resolveWindowAroundOrdinal(
          anchorMeta.ordinal,
          index.length,
          Math.min(CHUNK_WINDOW_MAX, Math.max(1, windowSpan)),
        );
        this.activeWindow = this.clampWindow(target);
        this.editorWindow = { ...this.activeWindow };
        this.syncActiveChunkFromWindow();
        this.materializeWindow(this.activeWindow);
      }

      chunkPerfLog('commit:saved-document-reindex', {
        blocks: nodes.length,
        window: this.activeWindow,
      });
      this.revision += 1;
      this.emit();
      return true;
    }

    this.sourceDocument = nodes as TranscriptBlock[];
    this.dirtyIds.clear();
    if (this.activeWindow) {
      this.rematerializeWindowFromSource(this.activeWindow);
    }

    chunkPerfLog('commit:saved-document', {
      blocks: nodes.length,
      window: this.activeWindow,
    });
    return true;
  }

  private readWindowAnchorTime(): number | null {
    if (!this.activeWindow) return null;
    const meta = this.index[this.activeWindow.startOrdinal];
    if (!meta) return null;
    const nodes = this.chunks[meta.chunkId];
    const start = nodes?.[0]?.data?.start ?? meta.startTime;
    return typeof start === 'number' ? start : null;
  }

  matchesDocument(nodes: unknown, documentKey?: string | null): boolean {
    if (!this.enabled || !this.index.length) return false;
    if (this.saveInFlight) return true;
    // Unsaved edits live only in the chunk store — do not re-init from Redux/S3.
    if (this.dirtyIds.size > 0) return true;

    if (!Array.isArray(nodes) || !nodes.length) return false;

    const incomingKey =
      documentKey ??
      computeTranscriptDocumentKey(nodes as TranscriptBlock[]);
    if (this.documentKey && incomingKey !== this.documentKey) {
      return false;
    }

    const total = this.index.reduce((sum, m) => sum + m.blockCount, 0);
    if (total !== nodes.length) return false;

    const sampleRows = [0, total >> 1, total - 1];
    return sampleRows.every((row) => {
      const incoming = (nodes as TranscriptBlock[])[row]?.data;
      const held = this.blockAtGlobalRow(row)?.data;
      return (
        !!incoming &&
        !!held &&
        incoming.start === held.start &&
        incoming.end === held.end
      );
    });
  }

  getDocumentKey() {
    return this.documentKey;
  }

  initFromFullDocument<T extends TranscriptBlock>(
    nodes: T[] | null | undefined,
    maxBlocks: number = CHUNK_MAX_BLOCKS,
    documentKey?: string | null,
  ) {
    const t0 = performance.now();
    const list = Array.isArray(nodes) ? nodes : [];
    this.documentKey =
      documentKey ?? computeTranscriptDocumentKey(list as TranscriptBlock[]);
    this.cancelPendingWindowDirect();
    this.playbackSeekTarget = null;
    this.playbackSeekSliding = false;
    this.findJumpTarget = null;
    this.findJumpLastOrdinal = null;
    const index = buildTranscriptChunkIndex(list, maxBlocks);
    this.index = index;
    this.sourceDocument = list.length ? (list as TranscriptBlock[]) : null;
    this.chunks = {};
    this.dirtyIds.clear();
    this.enabled = index.length > 0;
    if (this.isDwcm()) {
      const boot = dwcmInitialWindow(index.length);
      this.initWindow(boot.startOrdinal, boot.endOrdinal);
      this.materializeWindow(boot);
    } else {
      this.activeChunkId = index[0]?.chunkId ?? null;
      this.activeWindow = null;
      this.editorWindow = null;
      this.warmChunkId = index.length > 1 ? index[1].chunkId : null;
      if (index[0]) this.materializeChunk(index[0].chunkId);
      if (index[1]) this.materializeChunk(index[1].chunkId);
    }
    this.loadingChunkId = null;
    this.queuedChunkId = null;
    this.editorReady = !this.isDwcm();
    this.editorChunkId = null;
    this.playbackPausedForLoad = false;
    this.playbackResumeChunkId = null;
    this.scrollPinnedChunkId = null;
    this.documentRevision = 0;
    this.cancelPendingWindowDirect();

    const firstSrc = list[0]?.data?.start;
    const lastSrc = list[list.length - 1]?.data?.start;
    const firstMeta = index[0]?.startTime;
    const lastMeta = index[index.length - 1]?.endTime;
    chunkPerfLog('init:lazy-index', {
      sourceLen: list.length,
      chunkCount: index.length,
      bootWindow: this.activeWindow,
      indexMs: Number((performance.now() - t0).toFixed(1)),
      firstSrc,
      firstMeta,
      lastSrc,
      lastMeta,
    });

    this.emit();
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => this.dumpChunks('init-idle'));
    }
    return this.getSnapshot();
  }

  /**
   * Human-readable map of every chunk: block counts, real first/last block
   * times, and the gap to the next chunk. Use to prove whether transcript time
   * is actually missing from the store or only missing on screen.
   * Call `window.dumpTranscriptChunks()` from DevTools at any time.
   */
  dumpChunks(label = 'manual') {
    const rows: ChunkDumpRow[] = [];
    const problems: Record<string, unknown>[] = [];
    let totalBlocks = 0;

    for (let i = 0; i < this.index.length; i++) {
      const meta = this.index[i];
      const nodes = this.chunks[meta.chunkId];
      const blockCount = nodes?.length ?? meta.blockCount;
      const first =
        nodes?.[0]?.data?.start ??
        (typeof meta.startTime === 'number' ? meta.startTime : undefined);
      const lastNode = nodes?.[nodes.length - 1];
      const last =
        lastNode?.data?.end ??
        lastNode?.data?.start ??
        (typeof meta.endTime === 'number' ? meta.endTime : undefined);
      totalBlocks += blockCount;

      const nextMeta = this.index[i + 1];
      const nextNodes = nextMeta ? this.chunks[nextMeta.chunkId] : undefined;
      const nextFirst =
        nextNodes?.[0]?.data?.start ??
        (typeof nextMeta?.startTime === 'number' ? nextMeta.startTime : undefined);
      const gap =
        typeof last === 'number' && typeof nextFirst === 'number'
          ? nextFirst - last
          : null;

      const issues: string[] = [];
      if (blockCount === 0) issues.push('EMPTY');
      if (gap != null && gap > CHUNK_BOUNDARY_TOLERANCE_SEC) {
        issues.push(`GAP ${gap.toFixed(1)}s`);
      }
      if (gap != null && gap < -CHUNK_BOUNDARY_TOLERANCE_SEC) {
        issues.push(`OVERLAP ${Math.abs(gap).toFixed(1)}s`);
      }
      const prevNodes =
        i > 0 ? this.chunks[this.index[i - 1].chunkId] : undefined;
      const prevMeta = i > 0 ? this.index[i - 1] : undefined;
      const prevFirst =
        prevNodes?.[0]?.data?.start ??
        (typeof prevMeta?.startTime === 'number' ? prevMeta.startTime : undefined);
      if (
        typeof first === 'number' &&
        typeof prevFirst === 'number' &&
        first === prevFirst
      ) {
        issues.push('DUPLICATE-OF-PREV');
      }
      if (typeof first === 'number' && meta.startTime !== first) {
        issues.push('META-DRIFT');
      }

      const row: ChunkDumpRow = {
        ordinal: meta.ordinal,
        chunkId: meta.chunkId,
        blocks: blockCount,
        globalRows: `${meta.globalStartIndex}–${
          meta.globalStartIndex + blockCount - 1
        }`,
        firstStart: `${formatMediaTime(first)} (${first ?? '—'})`,
        lastEnd: `${formatMediaTime(last)} (${last ?? '—'})`,
        metaRange: `${formatMediaTime(meta.startTime)}–${formatMediaTime(
          meta.endTime,
        )}`,
        gapToNext: gap == null ? '—' : `${gap.toFixed(2)}s`,
        issue: issues.join(', ') || 'ok',
      };
      rows.push(row);
      if (issues.length) {
        problems.push({ ...row, firstStartSec: first, lastEndSec: last });
      }
    }

    const summary = {
      label,
      chunkCount: this.index.length,
      totalBlocks,
      activeChunkId: this.activeChunkId,
      editorChunkId: this.editorChunkId,
      dirtyChunks: Array.from(this.dirtyIds),
      problemCount: problems.length,
    };

    chunkDataGroup(
      `[chunk-data] ${label} — ${this.index.length} chunks, ${totalBlocks} blocks, ${problems.length} problem(s)`,
      () => {
        chunkDataLog('[chunk-data] summary', summary);
        // eslint-disable-next-line no-console
        console.table(rows);
        if (problems.length) {
          chunkDataWarn('[chunk-data] boundary problems', problems);
        }
      },
    );

    return { summary, rows, problems };
  }

  private neighborWarm(chunkId: string): string | null {
    const meta = this.getMeta(chunkId);
    if (!meta) return null;
    const nextId = this.index[meta.ordinal + 1]?.chunkId ?? null;
    const prevId =
      meta.ordinal > 0 ? this.index[meta.ordinal - 1]?.chunkId ?? null : null;
    const warmId = nextId && nextId !== chunkId ? nextId : prevId;
    return warmId === chunkId ? null : warmId;
  }

  /**
   * Request activation of a chunk. If a load is already in flight, queue the
   * latest target only (no parallel / infinite loads).
   * Returns: 'ready' | 'loading' | 'queued' | 'ignored'
   */
  requestChunkLoad(chunkId: string): 'ready' | 'loading' | 'queued' | 'ignored' {
    if (!chunkId || !this.getMeta(chunkId)) return 'ignored';
    this.materializeChunk(chunkId);

    // Already showing this chunk and editor is ready
    if (
      chunkId === this.activeChunkId &&
      this.editorReady &&
      !this.loadingChunkId
    ) {
      chunkPerfLog('requestChunkLoad:ready', { chunkId });
      return 'ready';
    }

    // Load in progress
    if (this.loadingChunkId) {
      if (chunkId === this.loadingChunkId || chunkId === this.activeChunkId) {
        chunkPerfLog('requestChunkLoad:already-loading', {
          chunkId,
          loadingChunkId: this.loadingChunkId,
          elapsedMs: this.getLoadElapsedMs(),
        });
        return 'loading';
      }
      // Only keep the latest queued target
      this.queuedChunkId = chunkId;
      chunkPerfLog('requestChunkLoad:queued', {
        chunkId,
        loadingChunkId: this.loadingChunkId,
        elapsedMs: this.getLoadElapsedMs(),
      });
      return 'queued';
    }

    const fromChunkId = this.activeChunkId;
    const flushT0 = performance.now();
    // Flush edits before leaving the chunk. Never replace with a shorter
    // Editor snapshot (boundary unmount can briefly drop trailing nodes).
    const flushTarget = this.flushTargetChunkId();
    if (window?.Editor?.children && flushTarget) {
      const nodes = window.Editor.children as TranscriptBlock[];
      const existing = this.chunks[flushTarget];
      if (!existing || nodes.length >= existing.length) {
        this.updateChunk(flushTarget, nodes, 'requestChunkLoad');
      } else {
        chunkPerfLog('requestChunkLoad:flush-skip-shorter', {
          chunkId: flushTarget,
          editorLen: nodes.length,
          storeLen: existing.length,
        });
      }
    }
    const flushMs = performance.now() - flushT0;

    const meta = this.getMeta(chunkId);
    const blockCount = this.chunks[chunkId]?.length ?? 0;
    this.loadStartedAt = performance.now();
    this.loadChunkId = chunkId;

    this.activeChunkId = chunkId;
    this.warmChunkId = this.neighborWarm(chunkId);
    this.loadingChunkId = chunkId;
    this.editorReady = false;
    this.queuedChunkId = null;

    chunkPerfLog('requestChunkLoad:start', {
      chunkId,
      fromChunkId,
      blockCount,
      ordinal: meta?.ordinal,
      flushPrevMs: Number(flushMs.toFixed(1)),
    });

    const emitT0 = performance.now();
    this.emit();
    chunkPerfLog('requestChunkLoad:emit', {
      chunkId,
      emitMs: Number((performance.now() - emitT0).toFixed(1)),
      listeners: this.listeners.size,
    });
    return 'loading';
  }

  /** Shell: skeleton painted; still not ready for playback. */
  markEditorMounting(chunkId: string) {
    if (this.loadingChunkId !== chunkId) return;
    // keep loading until markEditorReady
  }

  /** Shell: Slate mounted for chunk — safe for playback / highlight. */
  markEditorReady(chunkId: string) {
    if (this.activeChunkId !== chunkId) return;
    const totalMs =
      this.loadStartedAt != null
        ? performance.now() - this.loadStartedAt
        : null;
    const blockCount = this.chunks[chunkId]?.length ?? 0;

    this.loadingChunkId = null;
    this.editorReady = true;
    this.loadStartedAt = null;
    this.loadChunkId = null;
    this.editorReadyRevision += 1;
    this.clearPlaybackSeekTarget();
    this.emit();

    chunkPerfLog('markEditorReady:done', {
      chunkId,
      blockCount,
      totalLoadMs: totalMs != null ? Number(totalMs.toFixed(1)) : null,
      queuedNext: this.queuedChunkId,
      scrolling: this.scrolling,
    });

    // Do not start another load while the user is still scrolling
    if (this.scrolling) return;

    const queued = this.queuedChunkId;
    this.queuedChunkId = null;
    if (queued && queued !== chunkId) {
      chunkPerfLog('markEditorReady:drain-queue', { queued });
      this.requestChunkLoad(queued);
    }
  }

  setActive(chunkId: string | null) {
    if (!chunkId) return;
    this.requestChunkLoad(chunkId);
  }

  setWarm(chunkId: string | null) {
    if (chunkId === this.warmChunkId) return;
    if (chunkId && !this.getMeta(chunkId)) return;
    this.warmChunkId = chunkId;
    this.emit();
  }

  setActiveWithNeighbor(chunkId: string) {
    const result = this.requestChunkLoad(chunkId);
    return result === 'loading' || result === 'ready';
  }

  updateChunk<T extends TranscriptBlock>(
    chunkId: string,
    nodes: T[],
    source = 'unknown',
  ): boolean {
    if (!Array.isArray(nodes)) return false;
    const existing = this.chunks[chunkId];
    if (!existing) {
      if (!nodes.length) return false;
      this.chunks[chunkId] = nodes as TranscriptBlock[];
      this.dirtyIds.add(chunkId);
      recomputeGlobalStartIndices(this.index, this.chunks, this.dirtyIds);
      const meta = this.index.find((m) => m.chunkId === chunkId);
      if (meta && nodes.length) {
        const first = nodes[0]?.data?.start;
        const last = nodes[nodes.length - 1]?.data?.end;
        if (typeof first === 'number') meta.startTime = first;
        if (typeof last === 'number') meta.endTime = last;
      }
      return true;
    }

    if (!nodes.length && existing.length) {
      chunkPerfLog('updateChunk:skip-empty', {
        chunkId,
        source,
        storeLen: existing.length,
      });
      return false;
    }

    // Reject nodes that demonstrably belong to a different chunk. Without this,
    // an onChange fired between requestChunkLoad and the new editor mounting
    // overwrites the incoming chunk with the outgoing one's blocks.
    const incomingStart = nodes[0]?.data?.start;
    const currentStart = existing[0]?.data?.start;
    if (
      typeof incomingStart === 'number' &&
      typeof currentStart === 'number' &&
      incomingStart !== currentStart
    ) {
      const owner = this.index.find(
        (m) => m.chunkId !== chunkId && m.startTime === incomingStart,
      );
      if (owner) {
        chunkDataError(
          '[chunk-data] rejected cross-chunk write — editor nodes belong to another chunk',
          {
            target: chunkId,
            looksLike: owner.chunkId,
            source,
            incomingStart,
            currentStart,
            editorChunkId: this.editorChunkId,
            activeChunkId: this.activeChunkId,
          },
        );
        return false;
      }
    }

    this.chunks[chunkId] = nodes as TranscriptBlock[];
    this.dirtyIds.add(chunkId);
    recomputeGlobalStartIndices(this.index, this.chunks, this.dirtyIds);
    const meta = this.index.find((m) => m.chunkId === chunkId);
    if (meta && nodes.length) {
      const first = nodes[0]?.data?.start;
      const last = nodes[nodes.length - 1]?.data?.end;
      if (typeof first === 'number') meta.startTime = first;
      if (typeof last === 'number') meta.endTime = last;
    }
    return true;
  }

  /** Write editor nodes back to the chunk the editor is bound to. */
  flushActiveFromEditor(
    nodes: TranscriptBlock[] | null | undefined,
    source = 'flushActiveFromEditor',
  ): boolean {
    if (!nodes) return false;
    if (this.isDwcm() && this.editorWindow) {
      return this.flushEditorWindowToStore(nodes, source);
    }
    const target = this.flushTargetChunkId();
    if (!target) return false;
    if (this.editorChunkId && this.editorChunkId !== this.activeChunkId) {
      chunkPerfLog('flush:editor-lags-active', {
        editorChunkId: this.editorChunkId,
        activeChunkId: this.activeChunkId,
        source,
      });
    }
    return this.updateChunk(target, nodes, source);
  }

  /**
   * Playback/time resolve. Does NOT force-activate while loading.
   * Returns meta + whether the active editor is ready for this time.
   */
  resolveChunkForTime(
    time: number,
    opts?: { prefetchLeadSec?: number; playing?: boolean },
  ): {
    meta: TranscriptChunkMeta;
    needsLoad: boolean;
    ready: boolean;
  } | null {
    if (!this.enabled || !this.index.length) return null;
    const meta = findChunkMetaForTime(this.index, time);
    if (!meta) return null;

    if (this.isDwcm() && this.activeWindow) {
      const inWindow =
        meta.ordinal >= this.activeWindow.startOrdinal &&
        meta.ordinal <= this.activeWindow.endOrdinal;
      const ready = inWindow && this.editorReady && !this.loadingChunkId;
      return { meta, needsLoad: !inWindow, ready };
    }

    const ready =
      meta.chunkId === this.activeChunkId &&
      this.editorReady &&
      !this.loadingChunkId;

    const needsLoad = meta.chunkId !== this.activeChunkId || !ready;

    // Prefetch hint only when ready (no thrash while loading)
    if (ready && opts?.playing) {
      const lead = opts?.prefetchLeadSec ?? CHUNK_PLAYBACK_PREFETCH_LEAD_SEC;
      const next = this.index[meta.ordinal + 1];
      if (next && time >= next.startTime - lead) {
        // Queue neighbor only; do not start load until current stays ready
        // Actual load happens when playhead enters next or scroll settle.
      }
    }

    return { meta, needsLoad, ready };
  }

  /**
   * Playback resolution, not a raw range test. A playhead sitting in the
   * silence between two chunks is outside both ranges but resolves forward to
   * the next chunk — which is the chunk we paused and loaded for it.
   */
  isTimeInChunk(chunkId: string, time: number): boolean {
    const meta = findChunkMetaForTime(this.index, time);
    return !!meta && meta.chunkId === chunkId;
  }

  ensureChunkForTime(
    time: number,
    opts?: { prefetchLeadSec?: number; playing?: boolean; seek?: boolean },
  ): TranscriptChunkMeta | null {
    const resolved = this.resolveChunkForTime(time, opts);
    if (!resolved) return null;

    this.scrollPinnedChunkId = null;

    if (opts?.seek) {
      this.armPlaybackSeek(time);
    }

    if (this.isDwcm()) {
      if (this.playbackSeekSliding || opts?.seek) {
        return resolved.meta;
      }
      this.ensureWindowContainsOrdinal(resolved.meta.ordinal);
      return resolved.meta;
    }
    if (resolved.needsLoad) {
      this.requestChunkLoad(resolved.meta.chunkId);
    }
    return resolved.meta;
  }

  ensureWindowContainsGlobalRow(globalRow: number): TranscriptChunkMeta | null {
    if (this.isDwcm()) {
      return this.ensureFindJumpForGlobalRow(globalRow)?.meta ?? null;
    }
    recomputeGlobalStartIndices(this.index, this.chunks, this.dirtyIds);
    const meta = findChunkMetaForGlobalRow(this.index, globalRow, this.chunks);
    if (!meta) return null;
    this.scrollPinnedChunkId = null;
    this.requestChunkLoad(meta.chunkId);
    return meta;
  }

  ensureChunkForGlobalRow(globalRow: number): TranscriptChunkMeta | null {
    return this.ensureWindowContainsGlobalRow(globalRow);
  }

  ensureChunkForBlockStart(blockStart: number): TranscriptChunkMeta | null {
    const meta = findChunkMetaForBlockStart(
      this.index,
      this.chunks,
      blockStart,
    );
    if (!meta) return null;
    this.scrollPinnedChunkId = null;
    if (this.isDwcm()) {
      this.ensureWindowContainsOrdinal(meta.ordinal);
      return meta;
    }
    this.requestChunkLoad(meta.chunkId);
    return meta;
  }

  /**
   * Wait until the given chunk's Slate editor is mounted and ready
   * (used by Find jump after ensureChunkFor*).
   */
  waitUntilEditorReady(
    chunkId: string,
    opts?: { timeoutMs?: number },
  ): Promise<boolean> {
    const timeoutMs = opts?.timeoutMs ?? 8000;
    const readyNow = () => {
      if (!chunkId || !this.editorReady || this.loadingChunkId) return false;
      if (this.isDwcm() && this.activeWindow) {
        const ord = this.index.find((m) => m.chunkId === chunkId)?.ordinal;
        if (typeof ord !== 'number') return false;
        return (
          ord >= this.activeWindow.startOrdinal &&
          ord <= this.activeWindow.endOrdinal
        );
      }
      return this.activeChunkId === chunkId;
    };
    if (readyNow()) return Promise.resolve(true);

    return new Promise((resolve) => {
      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        unsub();
        resolve(ok);
      };

      const check = () => {
        if (readyNow()) finish(true);
      };

      const unsub = this.subscribe(() => check());
      const timer = window.setTimeout(() => {
        chunkPerfLog('waitUntilEditorReady:timeout', {
          chunkId,
          activeChunkId: this.activeChunkId,
          activeWindow: this.activeWindow,
          editorReady: this.editorReady,
          loadingChunkId: this.loadingChunkId,
          timeoutMs,
        });
        finish(false);
      }, timeoutMs);

      check();
    });
  }

  remapGlobalRangePathsToLocal<
    T extends {
      anchor?: { path?: number[]; offset?: number };
      focus?: { path?: number[]; offset?: number };
    },
  >(
    ranges: T[] | null | undefined,
  ): Map<number, { range: T; globalIndex: number }[]> {
    const map = new Map<number, { range: T; globalIndex: number }[]>();
    if (!ranges?.length) return map;
    recomputeGlobalStartIndices(this.index, this.chunks, this.dirtyIds);
    if (this.isDwcm() && this.activeWindow) {
      this.materializeWindow(this.activeWindow);
    }

    const window = this.isDwcm()
      ? this.activeWindow
      : this.getActiveMeta()
        ? {
            startOrdinal: this.getActiveMeta()!.ordinal,
            endOrdinal: this.getActiveMeta()!.ordinal,
          }
        : null;
    if (!window) return map;

    const windowStartGlobal =
      this.index[window.startOrdinal]?.globalStartIndex ?? 0;
    const lastMeta = this.index[window.endOrdinal];
    const windowEndGlobal =
      (lastMeta?.globalStartIndex ?? 0) + (lastMeta?.blockCount ?? 0);

    const rowNodeAtGlobal = (globalRow: number): TranscriptBlock | undefined => {
      for (let ord = window.startOrdinal; ord <= window.endOrdinal; ord++) {
        const meta = this.index[ord];
        if (!meta) continue;
        const nodes = this.chunks[meta.chunkId] ?? [];
        const start = meta.globalStartIndex;
        const end = start + meta.blockCount;
        if (globalRow >= start && globalRow < end) {
          return nodes[globalRow - start];
        }
      }
      return undefined;
    };

    const leafLen = (rowNode: TranscriptBlock | undefined, wordIdx: number) => {
      const leaf = rowNode?.children?.[wordIdx] as { text?: string } | undefined;
      return leaf?.text?.length ?? 0;
    };

    const clipOffset = (
      rowNode: TranscriptBlock | undefined,
      wordIdx: number,
      offset: number | undefined,
    ) => {
      const len = leafLen(rowNode, wordIdx);
      const o = offset ?? 0;
      return Math.max(0, Math.min(o, len));
    };

    const normalizeRange = (localRange: T): T => {
      const a = localRange.anchor;
      const f = localRange.focus;
      if (!a?.path || !f?.path) return localRange;
      const aPath = a.path;
      const fPath = f.path;
      const aOff = a.offset ?? 0;
      const fOff = f.offset ?? 0;
      const aBefore =
        aPath[0] < fPath[0] ||
        (aPath[0] === fPath[0] &&
          (aPath[1] < fPath[1] ||
            (aPath[1] === fPath[1] && aOff <= fOff)));
      if (aBefore) return localRange;
      return {
        ...localRange,
        anchor: f,
        focus: a,
      } as T;
    };

    for (let i = 0; i < ranges.length; i++) {
      const range = ranges[i];
      const aRow = range?.anchor?.path?.[0];
      const fRow = range?.focus?.path?.[0] ?? aRow;
      if (typeof aRow !== 'number') continue;

      const lo = typeof fRow === 'number' ? Math.min(aRow, fRow) : aRow;
      const hi = typeof fRow === 'number' ? Math.max(aRow, fRow) : aRow;

      for (let g = lo; g <= hi; g++) {
        if (g < windowStartGlobal || g >= windowEndGlobal) continue;

        const localRow = g - windowStartGlobal;
        const rowNode = rowNodeAtGlobal(g);
        const lastWordIdx = Math.max(0, (rowNode?.children?.length ?? 1) - 1);

        const anchorInWindow = aRow >= windowStartGlobal && aRow < windowEndGlobal;
        const focusInWindow =
          typeof fRow === 'number' &&
          fRow >= windowStartGlobal &&
          fRow < windowEndGlobal;

        let localAnchorPath: number[];
        let localAnchorOffset: number;
        let localFocusPath: number[];
        let localFocusOffset: number;

        if (g === aRow && g === fRow) {
          const aWord = range.anchor!.path![1] ?? 0;
          const fWord = range.focus?.path?.[1] ?? aWord;
          localAnchorPath = [localRow, aWord];
          localAnchorOffset = clipOffset(rowNode, aWord, range.anchor!.offset);
          localFocusPath = [localRow, fWord];
          localFocusOffset = clipOffset(rowNode, fWord, range.focus?.offset);
        } else if (g === aRow && anchorInWindow) {
          const aWord = range.anchor!.path![1] ?? 0;
          localAnchorPath = [localRow, aWord];
          localAnchorOffset = clipOffset(rowNode, aWord, range.anchor!.offset);
          localFocusPath = [localRow, lastWordIdx];
          localFocusOffset = leafLen(rowNode, lastWordIdx);
        } else if (g === fRow && focusInWindow) {
          const fWord = range.focus!.path![1] ?? 0;
          localAnchorPath = [localRow, 0];
          localAnchorOffset = 0;
          localFocusPath = [localRow, fWord];
          localFocusOffset = clipOffset(rowNode, fWord, range.focus!.offset);
        } else {
          localAnchorPath = [localRow, 0];
          localAnchorOffset = 0;
          localFocusPath = [localRow, lastWordIdx];
          localFocusOffset = leafLen(rowNode, lastWordIdx);
        }

        const localRange = normalizeRange({
          ...range,
          anchor: range.anchor
            ? {
                ...range.anchor,
                path: localAnchorPath,
                offset: localAnchorOffset,
              }
            : range.anchor,
          focus: range.focus
            ? {
                ...range.focus,
                path: localFocusPath,
                offset: localFocusOffset,
              }
            : range.focus,
        } as T);

        let list = map.get(localRow);
        if (!list) {
          list = [];
          map.set(localRow, list);
        }
        list.push({ range: localRange, globalIndex: i });
      }
    }
    return map;
  }
}

export const transcriptChunkStore = new TranscriptChunkStoreImpl();

/** Merge the active Slate editor into the chunk store before finder reads. */
export const flushActiveEditorToChunkStore = (source = 'flushActiveEditor') => {
  if (!transcriptChunkStore.isEnabled()) return;
  const nodes = (window as any)?.Editor?.children as
    | TranscriptBlock[]
    | undefined;
  if (!nodes?.length) return;

  if (transcriptChunkStore.isDwcm()) {
    const editorWindow = transcriptChunkStore.getEditorWindow();
    if (!editorWindow) return;
    const expected =
      transcriptChunkStore.expectedWindowBlockCount(editorWindow);
    if (expected > 0 && nodes.length !== expected) {
      chunkPerfLog('flush:skip-length-mismatch', {
        source,
        expected,
        actual: nodes.length,
        editorWindow,
      });
      return;
    }
  }

  transcriptChunkStore.flushActiveFromEditor(nodes, source);
};

export const dumpTranscriptChunks = (label = 'manual') =>
  transcriptChunkStore.dumpChunks(label);

if (typeof window !== 'undefined') {
  (window as any).dumpTranscriptChunks = dumpTranscriptChunks;
  (window as any).diagnoseTranscriptLinePlay = () => {
    const diagnosis = transcriptChunkStore.diagnoseTranscriptLinePlayBlock();
    logTranscriptLinePlayDiagnosis('manual', diagnosis, {}, true);
    return diagnosis;
  };
}

export const getTranscriptNodes = <
  T extends TranscriptBlock = TranscriptBlock,
>(): T[] | undefined => {
  if (transcriptChunkStore.isEnabled()) {
    return transcriptChunkStore.getStitched<T>();
  }
  const children = (window as any)?.Editor?.children as T[] | undefined;
  return children;
};
